import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  ArrowRight,
  RotateCcw,
  Sparkles,
  Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fireMilestone } from "@/lib/celebrate";
import ApertureLoader from "@/components/ApertureLoader";
import { Button } from "@/components/ui/button";

export default function FinalExam() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null); // { questions, total, pass_threshold_pct, last_score, ... }
  const [answers, setAnswers] = useState({}); // { qid: optionIndex }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { passed, score_pct, correct, total, correct_ids, incorrect_ids }

  const load = async () => {
    try {
      const { data } = await api.get("/photographer/final-exam");
      setMeta(data);
    } catch (e) {
      toast.error("Could not load the exam.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const totalAnswered = Object.keys(answers).length;
  const total = meta?.total || 0;
  const allAnswered = total > 0 && totalAnswered === total;

  const select = (qid, idx) => setAnswers((a) => ({ ...a, [qid]: idx }));

  const submit = async () => {
    if (!allAnswered) {
      toast.error("Please answer every question first.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/photographer/final-exam/submit", { answers });
      setResult(data);
      if (data.passed) {
        setTimeout(() => fireMilestone(), 250);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Could not submit the exam.");
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => {
    setAnswers({});
    setResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) return <ApertureLoader label="Loading exam…" />;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="final-exam-page">
      {/* Header */}
      <button
        onClick={() => navigate("/dashboard")}
        data-testid="final-exam-back-btn"
        className="inline-flex items-center gap-1.5 font-mono-tag text-[10px] text-[#78584B] hover:text-[#6B0F1A] mb-6"
      >
        <ArrowLeft className="w-3 h-3" strokeWidth={2} />
        BACK TO ONBOARDING
      </button>

      <div className="flex items-start gap-4 mb-2">
        <GraduationCap className="w-10 h-10 text-[#6B0F1A] mt-1" strokeWidth={1.6} />
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05] text-[#1A0F0A]">
            Final Exam
          </h1>
        </div>
      </div>
      <p className="text-[#4D3F35] mt-3 max-w-xl leading-relaxed">
        A short self-check on the onboarding docs. You need{" "}
        <strong className="text-[#1A0F0A]">{meta?.pass_threshold_pct}%</strong> to pass — you can retake it as
        many times as you need.
      </p>

      {/* Result panel */}
      <AnimatePresence>
        {result && (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            data-testid="final-exam-result"
            className={`mt-8 rounded-sm p-6 card-surface ${
              result.passed ? "border-[#6B0F1A]/40" : "border-[#B08D57]/40"
            }`}
          >
            <div className="flex items-start gap-4">
              {result.passed ? (
                <Trophy className="w-9 h-9 text-[#6B0F1A]" strokeWidth={1.75} />
              ) : (
                <RotateCcw className="w-9 h-9 text-[#B08D57]" strokeWidth={1.75} />
              )}
              <div className="flex-1">
                <div className="font-mono-tag text-[10px] text-[#78584B]">
                  {result.correct} / {result.total} correct
                </div>
                <div className="flex items-baseline gap-3 mt-1">
                  <div
                    className="font-display text-5xl font-extrabold tracking-tighter text-[#1A0F0A]"
                    data-testid="final-exam-score"
                  >
                    {result.score_pct}%
                  </div>
                  <div
                    className={`font-display text-xl font-bold ${
                      result.passed ? "text-[#6B0F1A]" : "text-[#B08D57]"
                    }`}
                    data-testid="final-exam-verdict"
                  >
                    {result.passed ? "Passed" : "Not quite yet"}
                  </div>
                </div>
                <p className="text-sm text-[#4D3F35] mt-3 leading-relaxed">
                  {result.passed
                    ? "Great work — your Final Exam checklist item is marked complete. Head back to your dashboard to keep going."
                    : `You need at least ${result.pass_threshold_pct}% to pass. Review the questions marked in gold below, then retake the exam.`}
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {result.passed ? (
                    <>
                      <Button
                        onClick={() => navigate("/dashboard")}
                        data-testid="final-exam-back-to-dashboard-btn"
                        className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold tracking-wide rounded-sm btn-primary-glow"
                      >
                        <Sparkles className="w-4 h-4 mr-2" strokeWidth={2} />
                        Back to my onboarding
                      </Button>
                      <Button
                        onClick={retake}
                        variant="ghost"
                        className="border border-[#1A0F0A]/12 text-[#78584B] hover:bg-[#1A0F0A]/5 font-mono-tag text-[10px]"
                      >
                        Retake for practice
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={retake}
                      data-testid="final-exam-retake-btn"
                      className="bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold tracking-wide rounded-sm"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" strokeWidth={2} />
                      Retake exam
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress */}
      {!result && (
        <div className="mt-8 mb-6 flex items-center gap-4">
          <div className="flex-1 h-1.5 bg-[#1A0F0A]/10 rounded-sm overflow-hidden">
            <div
              className="h-full bg-[#6B0F1A] transition-[width] duration-300"
              style={{ width: `${total ? (totalAnswered / total) * 100 : 0}%` }}
            />
          </div>
          <div className="font-mono-tag text-[10px] text-[#78584B]">
            {totalAnswered} / {total} ANSWERED
          </div>
        </div>
      )}

      {/* Questions */}
      <div className="space-y-6">
        {(meta?.questions || []).map((q, idx) => {
          const picked = answers[q.id];
          const showResult = !!result;
          const correctIdx = showResult ? result.correct_answers?.[q.id] : null;
          const wasWrong = showResult && result.incorrect_ids?.includes(q.id);
          return (
            <motion.div
              key={q.id}
              layout
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
              data-testid={`final-exam-q-${q.id}`}
              className={`card-surface rounded-sm p-6 ${
                showResult && wasWrong ? "border-[#6B0F1A]/50" : ""
              } ${showResult && !wasWrong ? "border-[#0E7C3A]/40" : ""}`}
            >
              <div className="font-mono-tag text-[10px] text-[#78584B] mb-2 flex items-center gap-2">
                <span>Q{idx + 1} · {q.topic}</span>
                {showResult && !wasWrong && (
                  <span className="text-[#0E7C3A] inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" strokeWidth={2.25} />
                    CORRECT
                  </span>
                )}
                {showResult && wasWrong && (
                  <span className="text-[#6B0F1A] inline-flex items-center gap-1">
                    <XCircle className="w-3 h-3" strokeWidth={2.25} />
                    INCORRECT
                  </span>
                )}
              </div>
              <h3 className="font-display text-lg font-semibold text-[#1A0F0A] leading-snug mb-4">
                {q.prompt}
              </h3>
              <div className="space-y-2">
                {q.options.map((opt, oi) => {
                  const isSelected = picked === oi;
                  const isCorrectOption = showResult && correctIdx === oi;
                  const isWrongPick = showResult && isSelected && !isCorrectOption;
                  // Border/background tone after submission
                  let cls = "border-[#1A0F0A]/15 bg-white text-[#1A0F0A]";
                  if (!showResult) {
                    cls = isSelected
                      ? "border-[#6B0F1A] bg-[#6B0F1A]/6 text-[#1A0F0A]"
                      : "border-[#1A0F0A]/15 bg-white text-[#1A0F0A] hover:border-[#6B0F1A]/40 hover:bg-[#6B0F1A]/4";
                  } else if (isCorrectOption) {
                    cls = "border-[#0E7C3A] bg-[#0E7C3A]/28 text-[#1A0F0A] font-medium";
                  } else if (isWrongPick) {
                    cls = "border-[#6B0F1A] bg-[#6B0F1A]/20 text-[#1A0F0A] font-medium";
                  } else {
                    cls = "border-[#1A0F0A]/12 bg-white/60 text-[#78584B]";
                  }
                  return (
                    <button
                      key={oi}
                      type="button"
                      onClick={() => !showResult && select(q.id, oi)}
                      disabled={showResult}
                      data-testid={`final-exam-q-${q.id}-opt-${oi}`}
                      className={`w-full text-left px-4 py-3 rounded-sm border flex items-center gap-3 transition-colors ${cls} ${
                        showResult ? "cursor-default" : ""
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          isSelected && !showResult ? "border-[#6B0F1A]" :
                          isCorrectOption ? "border-[#0E7C3A]" :
                          isWrongPick ? "border-[#6B0F1A]" :
                          "border-[#1A0F0A]/30"
                        }`}
                      >
                        {isSelected && !showResult && <div className="w-2 h-2 rounded-full bg-[#6B0F1A]" />}
                        {isCorrectOption && <div className="w-2 h-2 rounded-full bg-[#0E7C3A]" />}
                        {isWrongPick && <div className="w-2 h-2 rounded-full bg-[#6B0F1A]" />}
                      </div>
                      <span className="flex-1 text-sm">{opt}</span>
                      {isCorrectOption && (
                        <CheckCircle2 className="w-4 h-4 text-[#0E7C3A]" strokeWidth={2.25} />
                      )}
                      {isWrongPick && (
                        <XCircle className="w-4 h-4 text-[#6B0F1A]" strokeWidth={2.25} />
                      )}
                    </button>
                  );
                })}
              </div>
              {showResult && wasWrong && (
                <div className="mt-3 space-y-1">
                  <div className="font-mono-tag text-[10px]">
                    <span className="text-[#0E7C3A]">Correct answer:</span>{" "}
                    <span className="text-[#4D3F35]">{q.options[correctIdx]}</span>
                  </div>
                  {q.reference && (
                    <div className="font-mono-tag text-[9px] text-[#78584B]">
                      SOURCE · {q.reference.toUpperCase()}
                    </div>
                  )}
                </div>
              )}
              {showResult && !wasWrong && q.reference && (
                <div className="mt-3 font-mono-tag text-[9px] text-[#78584B]">
                  SOURCE · {q.reference.toUpperCase()}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Submit */}
      {!result && (
        <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
          <div className="font-mono-tag text-[10px] text-[#78584B]">
            {allAnswered ? "Ready to submit." : `${total - totalAnswered} question${total - totalAnswered === 1 ? "" : "s"} left`}
          </div>
          <Button
            onClick={submit}
            disabled={!allAnswered || submitting}
            data-testid="final-exam-submit-btn"
            className={`rounded-sm font-bold tracking-wide inline-flex items-center gap-2 ${
              allAnswered
                ? "bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] btn-primary-glow"
                : "bg-[#1A0F0A]/8 text-[#78584B] cursor-not-allowed"
            }`}
          >
            {submitting ? "Scoring…" : "Submit exam"}
            <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Button>
        </div>
      )}
    </div>
  );
}
